import type { DragEndEvent, DragMoveEvent } from "@dnd-kit/core";
import type { CategoryLink } from "@ext/navigation/NavigationLinks";
import type { RefObject } from "react";
import { useCallback } from "react";
import { navigationTreeStore } from "../store/navigationTreeStore";
import { parseBeforeItemSlotId } from "../utils/beforeItemSlot";
import { DropMode } from "../utils/dropMode";
import { parseGroupSlotId } from "../utils/groupSlotId";
import { isGroupNode } from "../utils/isGroupNode";
import { useNavigationDndBase } from "./useNavigationDndBase";

export type PutIntoGroup = (articlePath: string, group: CategoryLink, treeId?: string) => Promise<void>;

const resolveGroup = (overId: string): CategoryLink | null => {
	const { flatIndex, parentMap, childrenMap } = navigationTreeStore.getState();
	let current: string | undefined = parseGroupSlotId(overId)?.groupId ?? parseBeforeItemSlotId(overId) ?? overId;

	while (current) {
		const link = flatIndex[current];
		if (isGroupNode(link)) {
			const holdsSubgroups = (childrenMap[current] ?? []).some((id) => isGroupNode(flatIndex[id]));
			return holdsSubgroups ? null : link;
		}
		current = parentMap[current];
	}
	return null;
};

export const useGroupedNavigationDnd = (containerRef: RefObject<HTMLDivElement | null>, putIntoGroup: PutIntoGroup) => {
	const {
		sensors,
		onDragStart,
		onDragCancel,
		finishDrag,
		setDragTarget,
		startAutoScroll,
		cancelAutoExpand,
		trackPointer,
	} = useNavigationDndBase(containerRef);

	const onDragMove = useCallback(
		({ activatorEvent, delta, over }: DragMoveEvent) => {
			trackPointer({ activatorEvent, delta });
			startAutoScroll();
			cancelAutoExpand();

			const group = over ? resolveGroup(String(over.id)) : null;
			setDragTarget(group ? { anchorId: group.ref.path, parentId: null, mode: DropMode.Into } : null);
		},
		[setDragTarget, cancelAutoExpand, startAutoScroll, trackPointer],
	);

	const onDragEnd = useCallback(
		({ active }: DragEndEvent) => {
			const { dragTarget, flatIndex } = navigationTreeStore.getState();
			const group = dragTarget ? flatIndex[dragTarget.anchorId] : null;

			const articlePath = flatIndex[String(active.id)]?.ref?.path;
			if (isGroupNode(group) && articlePath) void putIntoGroup(articlePath, group, String(active.id));
			finishDrag();
		},
		[finishDrag, putIntoGroup],
	);

	return { sensors, onDragStart, onDragMove, onDragEnd, onDragCancel };
};
